import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../../config/app-config';
import { getResponseErrorMessage, getRequestErrorMessage } from '../../../utils/apiErrors';
import useDashboardNotifier from './useDashboardNotifier';
import { v4 as uuid } from 'uuid';
import axios from 'axios';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Table, TableHead,
    TableBody, TableRow, TableCell, Checkbox, CircularProgress,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import EditReleaseDialog from './dialogs/EditReleaseDialog';
import UploadProgressDialog from './dialogs/UploadProgressDialog';

import { secureFetch } from "../../../utils/secureFetch";

export default function ReleaseManagement({
    open, onClose, user, isAuthenticated, login,
    adminApiBase,
}) {
    const theme = useTheme();
    const { notify, notification } = useDashboardNotifier();
    const [saving, setSaving] = useState(false);

    /* ---------------- State ---------------- */
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        code: '', displayName: '', public: false, products: [], languages: [],
    });

    /* Progress-Dialog */
    const [progressOpen, setProgressOpen] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    /* ---------------- Upload-Helfer ---------------- */
    async function uploadHelpZipsSimple(meta, releaseFiles, accessToken, onPct) {
        /* Manifest + FormData bauen ----------------------------------- */
        const manifest = { version: meta.code, products: [] };
        const fd = new FormData();

        Object.entries(releaseFiles).forEach(([product, pState]) => {
            if (!pState.overwrite) return;
            const langZips = {};
            Object.entries(pState.uploads || {}).forEach(([lang, file]) => {
                if (!file) { langZips[lang] = null; return; }
                const partKey = `${product}_${lang}_${uuid().slice(0, 8)}`;
                langZips[lang] = partKey;
                fd.append(partKey, file, file.name);
            });
            if (Object.keys(langZips).length) manifest.products.push({ name: product, langZips });
        });

        if (!manifest.products.length)
            throw new Error('Kein Produkt / keine Dateien zum Hochladen gewählt');

        fd.append('manifest', JSON.stringify(manifest));

        /* Upload mit Progress ----------------------------------------- */
        await axios.post(`${adminApiBase}admin/releases/upload`, fd, {
            headers: { Authorization: `Bearer ${accessToken}` },
            onUploadProgress: ev => {
                if (ev.total) onPct(Math.min(99, Math.round(ev.loaded / ev.total * 100)));
            },
        });
    }

    /* ---------------- Drag-and-Drop ---------------- */
    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const reordered = Array.from(versions);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setVersions(reordered);

        try {
            const res = await secureFetch(`${adminApiBase}admin/versions/reorder`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ids_order: reordered.map(v => v._id) }),
            }, true);
            if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to reorder releases.'));
            notify('Release order updated.');
        } catch (error) {
            setVersions(versions);
            notify(getRequestErrorMessage(error, 'Failed to reorder releases.'), 'error');
        }
    };

    /* ---------------- Daten laden ---------------- */
    const fetchVersions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await secureFetch(`${adminApiBase}admin/versions`, {}, true);
            if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to load releases.'));
            setVersions(await res.json());
        } catch (error) {
            notify(getRequestErrorMessage(error, 'Failed to load releases.'), 'error');
        } finally {
            setLoading(false);
        }
    }, [adminApiBase, notify]);

    useEffect(() => {
        if (!open) return;
        if (!isAuthenticated) { login(); return; }
        fetchVersions();
    }, [open, isAuthenticated, login, fetchVersions]);

    /* ---------------- Form öffnen/schließen ------- */
    const handleOpenForm = (version) => {
        if (version) {
            setEditingId(version._id);
            setFormData({
                code: version.code, displayName: version.displayName, public: version.public,
                products: version.products, languages: version.languages,
            });
        } else {
            setEditingId(null);
            setFormData({ code: '', displayName: '', public: false, products: [], languages: [] });
        }
        setFormOpen(true);
    };

    const handleCloseForm = () => { if (!saving) setFormOpen(false); };

    /* ---------------- SPEICHERN ------------------- */
    const handleSave = async (meta, releaseFiles) => {
        if (saving) return;
        setSaving(true);
        let metadataSaved = false;
        /* 1) Release-Metadaten --------------------------------------- */
        const payload = {
            code: meta.code.trim(), displayName: meta.displayName.trim(),
            public: meta.public, products: meta.products, languages: meta.languages,
        };

        try {
            const url = editingId
                ? `${adminApiBase}admin/versions/${editingId}`
                : `${adminApiBase}admin/versions`;
            const method = editingId ? 'PUT' : 'POST';

            const res = await secureFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }, true);
            if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to save release.'));
            metadataSaved = true;

            /* 2) ZIP-Upload (falls Dateien) ---------------------------- */
            const hasFiles = Object.values(releaseFiles || {}).some(
                p => p.overwrite && Object.values(p.uploads || {}).some(Boolean)
            );

            if (hasFiles) {
                setProgressOpen(true);
                setUploadPct(0);

                await uploadHelpZipsSimple(payload, releaseFiles, user.access_token, setUploadPct);
                setUploadPct(100);          // sicherstellen, dass 100 % angezeigt wird
            }

            /* 3) Refresh & Dialog zu ---------------------------------- */
            setFormOpen(false);
            notify(hasFiles ? 'Release saved. Upload accepted for processing.' : 'Release saved.');
            await fetchVersions();
        } catch (err) {
            setProgressOpen(false);
            setUploadPct(0);
            const message = getRequestErrorMessage(err, 'Failed to save release.');
            notify(metadataSaved ? `Release details were saved, but the upload failed. ${message}` : message, 'error');
        } finally {
            setSaving(false);
            handleCloseForm();
        }
    };

    /* ---------------- Löschen -------------------- */
    const handleDelete = async (id) => {
        if (!window.confirm('Version wirklich löschen?')) return;
        try {
            const res = await secureFetch(`${adminApiBase}admin/versions/${id}`, {
                method: 'DELETE',
            }, true);
            if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to delete release.'));
            notify('Release deleted.');
            await fetchVersions();
        } catch (error) {
            notify(getRequestErrorMessage(error, 'Failed to delete release.'), 'error');
        }
    };

    /* ---------------- Render --------------------- */
    return (
        <>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" noWrap sx={{ fontFamily: FONTS.heading, fontWeight: 700, mt: 2 }}>
                    Manage Releases
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => handleOpenForm()}
                        sx={{ backgroundColor: theme.palette.primary.main, mt: 1, color: '#fff' }}
                    >
                        + Add new Version
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Version</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Display Name</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Public</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Products</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Languages</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="versions" direction="vertical">
                                {(provided) => (
                                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                        {versions.map((v, i) => (
                                            <Draggable key={v._id} draggableId={String(v._id)} index={i}>
                                                {(prov, snap) => (
                                                    <TableRow
                                                        ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                                        sx={{ bgcolor: snap.isDragging ? 'action.hover' : 'inherit', cursor: 'grab' }}
                                                    >
                                                        <TableCell>{v.code}</TableCell>
                                                        <TableCell>{v.displayName}</TableCell>
                                                        <TableCell><Checkbox checked={v.public} disabled /></TableCell>
                                                        <TableCell>{v.products.join(', ')}</TableCell>
                                                        <TableCell>{v.languages.join(', ')}</TableCell>
                                                        <TableCell>
                                                            <Button size="small" sx={{ color: 'accent.main' }} onClick={() => handleOpenForm(v)}>Edit</Button>
                                                            <Button size="small" color="error" onClick={() => handleDelete(v._id)}>
                                                                Delete
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Table>
                )}
            </Box>

            {/* ---------- Dialoge ---------- */}
            <EditReleaseDialog
                open={formOpen}
                onClose={handleCloseForm}
                formData={formData}
                onChange={(e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }))}
                onTogglePublic={(e) => setFormData(p => ({ ...p, public: e.target.checked }))}
                onProductsChange={(v) => setFormData(p => ({ ...p, products: v }))}
                onLanguagesChange={(v) => setFormData(p => ({ ...p, languages: v }))}
                onSave={handleSave}
                saving={saving}
                isEditing={!!editingId}
            />

            <UploadProgressDialog
                open={progressOpen}
                pct={uploadPct}
                onClose={() => { setProgressOpen(false); setUploadPct(0); }}
            />
            {notification}
        </>
    );
}

